import React, { useEffect, useMemo, useState } from "react";
import {
    useFrontendErrorsGroups,
    useFrontendErrorsSummary,
    type FrontendErrorsError,
    type FrontendErrorsResource,
    type FrontendErrorsStatus,
} from "@/features/observability/hooks/use-frontend-errors";
import { frontendErrorsRange } from "@/features/observability/hooks/frontend-errors-range";
import { frontendErrorsFilters } from "@/features/observability/components/frontend-errors-filters";
import { CommandCenterErrorsByTypeCard, CommandCenterErrorsImpactCard } from "@/features/observability/components/CommandCenterErrorsCards";
import type { CommandCenterErrorsWindow } from "@/features/observability/components/CommandCenterErrorsModel";
import type { FrontendErrorsGroups, FrontendErrorsQueryParams, FrontendErrorsSummary } from "@/features/observability/types/frontend-errors.types";

type BoundaryState = { failed: boolean };

// FR-009: whatever these cards hit while rendering stays inside them; the rest of the page keeps working.
class CommandCenterErrorsBoundary extends React.Component<{ children: React.ReactNode }, BoundaryState> {
    state: BoundaryState = { failed: false };

    static getDerivedStateFromError(): BoundaryState {
        return { failed: true };
    }

    render(): React.ReactNode {
        if (this.state.failed) {
            return <p className="daily-panel cc-errors__notice cc-errors__row-failed">Las tarjetas de errores del frontend no están disponibles.</p>;
        }
        return this.props.children;
    }
}

type RowProps = { workspaceId: string; refreshedAt: number; attention: boolean };

// The series' step: a window end inside the same 5 minutes asks the reader nothing new.
const WINDOW_STEP_MS = 5 * 60 * 1000;
const UNSUPPORTED_TEXT = "Errores del frontend disponibles solo para EnvíaDinero";

type Snapshot = {
    range: CommandCenterErrorsWindow;
    summary: FrontendErrorsResource<FrontendErrorsSummary>;
    groups: FrontendErrorsResource<FrontendErrorsGroups>;
};

const sameError = (left: FrontendErrorsError | null, right: FrontendErrorsError | null): boolean => {
    if (left === null || right === null) return left === right;
    return left.code === right.code && left.message === right.message;
};

// By value, not by object: a resource re-read with the same answer must not count as a new settle, or the copy
// below would re-render forever.
const sameResource = <T,>(left: FrontendErrorsResource<T>, right: FrontendErrorsResource<T>): boolean =>
    left.status === right.status && left.data === right.data && sameError(left.error, right.error);

const sameSnapshot = (left: Snapshot, right: Snapshot): boolean =>
    left.range === right.range && sameResource(left.summary, right.summary) && sameResource(left.groups, right.groups);

const inFlight = (status: FrontendErrorsStatus): boolean => status === "idle" || status === "loading";

const CommandCenterErrorsCardsLoader: React.FC<RowProps> = ({ workspaceId, refreshedAt, attention }) => {
    // The 24 h window ends at the page's last refresh, so it moves with the rest of Salud and its links match it.
    const end = refreshedAt - (refreshedAt % WINDOW_STEP_MS);
    const range = useMemo((): CommandCenterErrorsWindow => ({ workspaceId, ...frontendErrorsRange.presetRange("24h", end) }), [workspaceId, end]);
    const query = useMemo((): FrontendErrorsQueryParams => ({
        workspaceId: range.workspaceId,
        source: "frontend",
        from: range.from,
        to: range.to,
        category: null,
        errorCode: null,
        release: null,
    }), [range]);
    const summary = useFrontendErrorsSummary(query);
    const asOf = summary.data === null ? null : summary.data.asOf;
    const groupsQuery = useMemo(() => (asOf === null ? null : { ...query, asOf, includeSample: true }), [query, asOf]);
    const groups = useFrontendErrorsGroups(groupsQuery);
    // A moved window refetches; until it settles the previous window stays on screen with its own links.
    const settled = !inFlight(summary.status) && (summary.data === null || !inFlight(groups.status));
    const current: Snapshot = { range, summary, groups };
    const [lastSettled, setLastSettled] = useState<Snapshot | null>(null);
    useEffect(() => {
        if (!settled) return;
        const next: Snapshot = { range, summary, groups };
        setLastSettled((previous) => (previous !== null && sameSnapshot(previous, next) ? previous : next));
    }, [settled, range, summary, groups]);
    const shown = settled || lastSettled === null ? current : lastSettled;
    return (
        <div className="cc-errors-row">
            <CommandCenterErrorsByTypeCard range={shown.range} summary={shown.summary} groups={shown.groups} attention={attention} />
            <CommandCenterErrorsImpactCard range={shown.range} summary={shown.summary} />
        </div>
    );
};

const UnsupportedCard: React.FC<{ title: string }> = ({ title }) => (
    <section className="daily-panel cc-errors" aria-label={title}>
        <div className="daily-panel__head">
            <h2>{title}</h2>
        </div>
        <p className="cc-errors__notice">{UNSUPPORTED_TEXT}</p>
    </section>
);

// The reader only answers for the screen's workspaces: any other one is not asked, and says why instead of «sin acceso».
export const CommandCenterErrorsRow: React.FC<RowProps> = ({ workspaceId, refreshedAt, attention }) => (
    <CommandCenterErrorsBoundary>
        {frontendErrorsFilters.SUPPORTED_WORKSPACES.includes(workspaceId) ? (
            <CommandCenterErrorsCardsLoader workspaceId={workspaceId} refreshedAt={refreshedAt} attention={attention} />
        ) : (
            <div className="cc-errors-row">
                <UnsupportedCard title="Errores del frontend por tipo" />
                <UnsupportedCard title="Impacto de los errores" />
            </div>
        )}
    </CommandCenterErrorsBoundary>
);
